import React from 'react';
import PropTypes from 'prop-types';

import { RichText } from '$partials/global';
import Newsletter from '$partials/modules/individual/newsletter';
import { useLocale } from '$contexts/locale';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const Logo = () => {
    return (
        <svg
            style={{ width: '95px', height: '20px' }}
            viewBox='0 0 95 20'
            xmlns='http://www.w3.org/2000/svg'>
            <title>Strava</title>
            <path
                clipRule='evenodd'
                d='M50.635 18.2831L50.6345 18.2822H55.65L58.7312 11.8896L61.8121 18.2822H67.9068L58.7306 0L50.0201 17.3566L46.6757 12.3175C48.7407 11.2898 50.0278 9.51011 50.0278 6.9536V6.90327C50.0278 5.09887 49.4936 3.79535 48.4733 2.74247C47.283 1.51434 45.3642 0.737362 42.3524 0.737362H34.0451V18.2831H39.729V13.2699H40.9434L44.1497 18.2831H50.635ZM85.6855 0L76.5105 18.2822H82.6051L85.6861 11.8896L88.7672 18.2822H94.8617L85.6855 0ZM72.2135 19.0016L81.3886 0.719372H75.2941L72.2129 7.11206L69.1318 0.719372H63.0373L72.2135 19.0016ZM42.1824 9.08402C43.5423 9.08402 44.3683 8.45742 44.3683 7.37968V7.32935C44.3683 6.20148 43.518 5.65007 42.2066 5.65007H39.729V9.08402H42.1824ZM21.8898 5.7002H16.8863V0.737362H32.5774V5.7002H27.5739V18.2831H21.8898V5.7002ZM3.03647 11.9164L0 15.6511C2.16189 17.6065 5.27103 18.6089 8.72002 18.6089C13.2867 18.6089 16.2258 16.3529 16.2258 12.6681V12.6182C16.2258 9.08402 13.311 7.7805 8.9631 6.9536C7.16555 6.6023 6.70406 6.30194 6.70406 5.82552V5.77539C6.70406 5.34931 7.09288 5.04854 7.94278 5.04854C9.52155 5.04854 11.4407 5.57488 13.0436 6.77795L15.8129 2.81786C13.8453 1.21357 11.4165 0.41133 8.13729 0.41133C3.44899 0.41133 0.922969 2.99331 0.922969 6.3268V6.37713C0.922969 10.0866 4.2752 11.2149 8.08851 12.0165C9.91036 12.3926 10.4445 12.6681 10.4445 13.1696V13.22C10.4445 13.696 10.0075 13.9715 8.9872 13.9715C6.99553 13.9715 4.88241 13.3703 3.03647 11.9164Z'
                fill='black'
                fillRule='evenodd' />
        </svg>
    );
};

const Section = ({
    title,
    body,
    domain
}) => {
    return (
        <li className='w-1/2 md:w-1/4 mb-40 px-10 sm:px-20 md:px-10'>
            <h3 className='uppercase text-dark-grey font-semibold mb-15'>
                {title}
            </h3>
            <RichText
                className='footer__rich-text'
                domain={domain}
                {...body} />
        </li>
    );
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const Footer = ({ hideBorder = false, menu, domain, showNewsletter }) => {
    const { translate: t } = useLocale();

    if (!menu) { return null; }
    return (
        <>

            <section className={`mx-10 sm:mx-5 md:mx-10 border-t border-light-grey ${hideBorder ? '' : 'border-t border-light-grey'} ${showNewsletter === false ? 'hidden' : 'block'}`}>
                <Newsletter
                    body={menu.newsletterBody}
                    title={menu.newsletterTitle}/>
            </section>
            <footer className={'type-style-10 pt-40 md:pt-60 mx-10 sm:mx-5 md:mx-10 border-t border-light-grey'}>
                <div className='flex flex-wrap -mx-10 sm:-mx-5 md:-mx-10'>
                    <div className='w-full sm:w-2/6 md:w-4/12 mb-40 px-10 sm:px-5 md:px-10'>
                        <Logo />
                        {menu?.copyright &&
                        <RichText
                            className='footer__rich-text mt-[43px]'
                            domain={domain}
                            {...menu.copyright} />
                        }
                    </div>

                    <ul className='flex flex-wrap w-full sm:w-4/6 md:w-8/12'>
                        {menu.column1 &&
                        <Section
                            body={menu.column1}
                            domain={domain}
                            title={menu.column1Title} />
                        }
                        {menu.column2 &&
                        <Section
                            body={menu.column2}
                            domain={domain}
                            title={menu.column2Title} />
                        }
                        {menu.column3 &&
                        <Section
                            body={menu.column3}
                            domain={domain}
                            title={menu.column3Title} />
                        }
                        {menu.column4 &&
                        <Section
                            body={menu.column4}
                            domain={domain}
                            title={menu.column4Title} />
                        }
                    </ul>

                    <div className='w-full border-t flex flex-col sm:flex-row sm:justify-between border-dark-grey text-dark-grey uppercase font-semibold py-20 md:py-30 mx-10 sm:mx-5 md:mx-10'>
                        <button
                            className='font-normal self-start mt-15 sm:mt-0 ml-auto'
                            onClick={() => window?.Cookiebot?.show()}
                            type='button'>
                            {t('Manage Cookies')}
                        </button>
                    </div>
                </div>
            </footer>
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

Footer.propTypes = {
    domain: PropTypes.string,
    hideBorder: PropTypes.bool,
    menu: PropTypes.shape({
        copyright: PropTypes.object,
        column1: PropTypes.object,
        column2: PropTypes.object,
        column3: PropTypes.object,
        column4: PropTypes.object,
        column1Title: PropTypes.string,
        column2Title: PropTypes.string,
        column3Title: PropTypes.string,
        column4Title: PropTypes.string,
        newsletterBody: PropTypes.string,
        newsletterTitle: PropTypes.string,
    }),
    showNewsletter: PropTypes.bool,
};

Section.propTypes = {
    domain: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ])
};

export default Footer;
